import React from "react";
import { Helmet } from "react-helmet-async";
import { useDynamicSEO } from "../../hooks/useDynamicSEO";

const DynamicSEOTags = ({
  fallbackTitle = "Polani Travels - Your Travel Partner",
  fallbackDescription = "Book your next journey with Polani Travels. We offer the best travel packages and services.",
  fallbackKeywords = "travel packages, tours, polani",
  fallbackOgTitle = "Polani Travels - Your Travel Partner",
  fallbackOgDescription = "Book your next journey with Polani Travels. We offer the best travel packages and services.",
  fallbackOgImage = "",
  fallbackOgUrl = "",
  fallbackCanonicalUrl = "",
  fallbackAuthor = "Polani Travels",
  fallbackRobots = "index, follow",
}) => {
  const { seoSettings, loading, error, hasSettings } = useDynamicSEO();

  // If loading, show basic SEO
  if (loading) {
    return (
      <Helmet>
        <title>{fallbackTitle}</title>
        <meta name="description" content={fallbackDescription} />
        <meta name="keywords" content={fallbackKeywords} />
        <meta property="og:title" content={fallbackOgTitle} />
        <meta property="og:description" content={fallbackOgDescription} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
    );
  }

  // If error or no settings, use fallback
  if (error || !hasSettings) {
    return (
      <Helmet>
        <title>{fallbackTitle}</title>
        <meta name="description" content={fallbackDescription} />
        <meta name="keywords" content={fallbackKeywords} />
        <meta property="og:title" content={fallbackOgTitle} />
        <meta property="og:description" content={fallbackOgDescription} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        {fallbackOgImage && (
          <meta property="og:image" content={fallbackOgImage} />
        )}
        {fallbackOgUrl && <meta property="og:url" content={fallbackOgUrl} />}
        {fallbackCanonicalUrl && (
          <link rel="canonical" href={fallbackCanonicalUrl} />
        )}
        <meta name="author" content={fallbackAuthor} />
        <meta name="robots" content={fallbackRobots} />
      </Helmet>
    );
  }

  // Use dynamic SEO settings from Firebase
  return (
    <Helmet prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{seoSettings.title}</title>
      <meta name="description" content={seoSettings.description} />
      <meta name="keywords" content={seoSettings.keywords} />

      {/* Canonical URL */}
      {seoSettings.canonicalUrl && (
        <link rel="canonical" href={seoSettings.canonicalUrl} />
      )}

      {/* Robots Meta */}
      <meta name="robots" content={seoSettings.robots} />

      {/* Author */}
      {seoSettings.author && (
        <meta name="author" content={seoSettings.author} />
      )}

      {/* Open Graph Tags */}
      <meta
        property="og:title"
        content={seoSettings.ogTitle || seoSettings.title}
      />
      <meta
        property="og:description"
        content={seoSettings.ogDescription || seoSettings.description}
      />
      {seoSettings.ogImage && (
        <meta property="og:image" content={seoSettings.ogImage} />
      )}
      <meta property="og:type" content={seoSettings.ogType} />
      {seoSettings.ogUrl && (
        <meta property="og:url" content={seoSettings.ogUrl} />
      )}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={seoSettings.twitterCard} />
      <meta
        name="twitter:title"
        content={seoSettings.twitterTitle || seoSettings.title}
      />
      <meta
        name="twitter:description"
        content={seoSettings.twitterDescription || seoSettings.description}
      />
      {seoSettings.twitterImage && (
        <meta name="twitter:image" content={seoSettings.twitterImage} />
      )}

      {/* Additional Technical Tags */}
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <html lang="en" />
    </Helmet>
  );
};

export default DynamicSEOTags;
